import React from 'react';
import { useTranslation } from 'react-i18next';
import './FormGenerator.scss';
import '../../../styles/GenericSiteStyles.scss';
import TextInput from '../../widgets/TextInput';
import NumberInput from '../../widgets/NumberInput';
import SelectInput from '../../widgets/SelectInput';
import CheckboxInput from '../../widgets/CheckboxInput';
import DateInput from '../../widgets/DateInput';
import DateTimeInput from '../../widgets/DateTimeInput';
import Slider from '../../widgets/Slider';
import MultilineTextBox from '../../widgets/MultilineTextBox';
import { toast } from 'react-toastify';

export type FormFieldType = 'string' | 'number' | 'select' | 'checkbox' | 'date' | 'slider' | 'datetime' | 'multiline';

export interface FormFieldConfig {
  key: string;
  label: string;
  type: FormFieldType;
  dataField?: string; // allows mapping to a specific JSON field
  options?: { label: string; value: any }[]; // for select
  required?: boolean;
  min?: number;
  max?: number;
  disabled?: boolean | ((data: any) => boolean); // NEW: can be static or dynamic
  multiline?: boolean; // for string fields, if true use multiline
  rows?: number; // for multiline
  defaultValue?: any; // initial value when opening the add form
}

export interface FormGeneratorProps<T> {
  fields: FormFieldConfig[];
  initialData: T;
  onChange?: (data: T) => void;
  onSubmit?: (data: T) => void;
  submitLabel?: string;
}

function FormGenerator<T>({ fields, initialData, onChange, onSubmit, submitLabel }: FormGeneratorProps<T>) {
  const { t } = useTranslation(['common', 'validation']);
  const resolvedSubmitLabel = submitLabel ?? t('common:save');
  // If no initialData, create an empty object with all field keys
  const emptyData = fields.reduce((acc, field) => {
    acc[field.key] = field.type === 'checkbox' ? false : '';
    return acc;
  }, {} as any);
  const [formData, setFormData] = React.useState<T>(initialData || emptyData);

  React.useEffect(() => {
    setFormData(initialData || emptyData);
  }, [initialData]);

  const handleFieldChange = (key: string, value: any) => {
    const updated = { ...formData, [key]: value };
    setFormData(updated);
    onChange?.(updated);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // Validation: check required fields
    const dataObj = formData as Record<string, any>;
    const missingFields = fields.filter(field => field.required && (dataObj[field.key] === '' || dataObj[field.key] === undefined || dataObj[field.key] === null));
    if (missingFields.length > 0) {
      toast.error(t('validation:requiredFields'));
      return;
    }
    onSubmit?.(formData);
  };

  return (
    <form onSubmit={handleSubmit} className="form-generator wide-form-generator">
      {fields.map(field => {
        const value = formData[field.key as keyof T] ?? (field.type === 'number' ? 0 : '');
        // Support dynamic or static disabled
        const isDisabled = typeof field.disabled === 'function' ? field.disabled(formData) : field.disabled;
        switch (field.type) {
          case 'string':
            if (field.multiline) {
              return <div><MultilineTextBox key={field.key} label={field.label} value={value as string} required={field.required} disabled={isDisabled} rows={field.rows} onChange={(v: string) => handleFieldChange(field.key, v)} /></div>;
            }
            return <div><TextInput key={field.key} label={field.label} value={value as string} required={field.required} disabled={isDisabled} onChange={(v: string) => handleFieldChange(field.key, v)} /></div>;
          case 'multiline':
            return <div><MultilineTextBox key={field.key} label={field.label} value={value as string} required={field.required} disabled={isDisabled} rows={field.rows} onChange={(v: string) => handleFieldChange(field.key, v)} /></div>;
          case 'number':
            return <div><NumberInput key={field.key} label={field.label} value={value as number} required={field.required} min={field.min} max={field.max} disabled={isDisabled} onChange={(v: number) => handleFieldChange(field.key, v)} /></div>;
          case 'select':
            return <div><SelectInput key={field.key} label={field.label} value={value} options={field.options || []} required={field.required} disabled={isDisabled} onChange={(v: any) => handleFieldChange(field.key, v)} /></div>;
          case 'checkbox':
            return <div><CheckboxInput key={field.key} label={field.label} checked={!!value} disabled={isDisabled} onChange={(v: boolean) => handleFieldChange(field.key, v)} /></div>;
          case 'date':
            return <div><DateInput key={field.key} label={field.label} value={value as string} required={field.required} disabled={isDisabled} onChange={(v: string) => handleFieldChange(field.key, v)} /></div>;
          case 'datetime':
            return <div><DateTimeInput key={field.key} label={field.label} value={value as string} required={field.required} disabled={isDisabled} onChange={(v: string) => handleFieldChange(field.key, v)} /></div>;
          case 'slider':
            return (
              <div key={field.key} className="mb-3">
                <label htmlFor={field.key}>{field.label}{field.required && ' *'}</label>
                <Slider
                  min={field.min ?? 0}
                  max={field.max ?? 5}
                  value={value as number}
                  disabled={isDisabled}
                  onChange={(v: number) => handleFieldChange(field.key, v)}
                  labels={field.options?.map(opt => opt.label)}
                />
              </div>
            );
          default:
            return null;
        }
      })}
      <div className="form-actions">
        <button type="submit" className="btn btn-primary">{resolvedSubmitLabel}</button>
      </div>
    </form>
  );
}

export default FormGenerator;
